import { getClient } from '@gonfalon/openapi/client';
import { deleteAccountToken } from '@gonfalon/openapi/operations/deleteAccountToken';
import { getAccount } from '@gonfalon/openapi/operations/getAccount';
import { getSamlConfig } from '@gonfalon/openapi/operations/getSamlConfig';
import { getScimConfig } from '@gonfalon/openapi/operations/getScimConfig';
import { getVersions } from '@gonfalon/openapi/operations/getVersions';
import { internalGetUnverifiedMemberPendingInvites } from '@gonfalon/openapi/operations/internalGetUnverifiedMemberPendingInvites';
import { patchAccount } from '@gonfalon/openapi/operations/patchAccount';
import { postCreateUnverifiedMember } from '@gonfalon/openapi/operations/postCreateUnverifiedMember';
import { type PostVerifyCodeRequestBody } from '@gonfalon/openapi/operations/postVerifyCode';
import type { Account } from '@gonfalon/openapi/types';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';

export type VerifyEmailCodeInput = {
  body: PostVerifyCodeRequestBody;
  referer?: string | null;
  signupSource?: string | null;
};

async function postVerifyCodeWithSignupContext({ body, referer, signupSource }: VerifyEmailCodeInput) {
  const params = new URLSearchParams();
  if (referer) {
    params.set('referer', referer);
  }
  if (signupSource) {
    params.set('signup_source', signupSource);
  }

  const query = params.toString();
  const path = '/internal/account/verify-code' as const;
  const url = query ? `${path}?${query}` : path;
  const client = getClient();
  const postRequest = client.POST.bind(client);

  return postRequest(url as '/internal/account/verify-code', {
    params: {},
    body,
  });
}

export const getAccountQuery = createQueryOptions(getAccount);
export const useUpdateAccount = createMutationHook(patchAccount, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getAccountQuery.partialQueryKey() });
  },
});
export const getSamlConfigQuery = createQueryOptions(getSamlConfig);
export const getScimConfigQuery = createQueryOptions(getScimConfig);

export const useCreateUnverifiedMember = createMutationHook(postCreateUnverifiedMember, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getAccountQuery.partialQueryKey() });
  },
});

export const useVerifyEmailCode = createMutationHook(postVerifyCodeWithSignupContext);

export const getPendingInvitesQuery = createQueryOptions(internalGetUnverifiedMemberPendingInvites);

export const getAPIVersionsQuery = createQueryOptions(getVersions);

export const useDeleteAccountToken = createMutationHook(deleteAccountToken, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    // Clear the legacy token from the cached account immediately so the banner
    // stops showing (and offering to copy) the now-deleted token without waiting
    // for the refetch to land — this mirrors the legacy flow's synchronous clear
    // of `account.tokens`. The invalidate below then reconciles with the server.
    queryClient.setQueriesData<Account>({ queryKey: getAccountQuery.partialQueryKey() }, (account) =>
      account ? { ...account, tokens: [] } : account,
    );
    await queryClient.invalidateQueries({ queryKey: getAccountQuery.partialQueryKey() });
  },
});
